/* =========================================================================
   Settings → Language Support: field definitions + pure helpers.

   Config fields (GET /config, saved with POST /config):
     enable_language_support  boolean   (default false)
     enabled_languages        string[]  (default all six; English always on)
     default_language         string    (default "en"; must be enabled)

   The backend mirrors these rules in media-platform
   src/utils/configLanguage.js and answers 422 + a per-field `errors` map.
   The website reads the same values (also as camelCase aliases).
   ========================================================================= */

export const FALLBACK_LANGUAGE = "en";

export const LANGUAGES = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "ar", name: "Arabic", flag: "🇸🇦" },
  { code: "fr", name: "French", flag: "🇫🇷" },
  { code: "hi", name: "Hindi", flag: "🇮🇳" },
  { code: "es", name: "Spanish", flag: "🇪🇸" },
  { code: "zh-TW", name: "Traditional Chinese", flag: "🇹🇼" },
];

export const LANGUAGE_CODES = LANGUAGES.map((l) => l.code);
export const LANGUAGE_FIELD_NAMES = ["enable_language_support", "enabled_languages", "default_language"];

export const languageName = (code) => LANGUAGES.find((l) => l.code === code)?.name || code;

// Known codes only, deduped, in LANGUAGES order, English always included.
export const normalizeEnabled = (codes) => {
  const list = Array.isArray(codes) ? codes : LANGUAGE_CODES;
  return LANGUAGE_CODES.filter((c) => c === FALLBACK_LANGUAGE || list.includes(c));
};

/** Form values from a /config row (missing fields -> card defaults). */
export const fromConfig = (config) => {
  const enabled = normalizeEnabled(config?.enabled_languages);
  const def = config?.default_language;
  return {
    enable_language_support: Boolean(config?.enable_language_support),
    enabled_languages: enabled,
    default_language: enabled.includes(def) ? def : FALLBACK_LANGUAGE,
  };
};

/**
 * Check / uncheck one language.
 * English can't be unchecked. Unchecking the current default resets the
 * default to English and returns a note to show inline.
 * @returns {{ values: object, note: string }}
 */
export const toggleLanguage = (values, code, checked) => {
  if (!LANGUAGE_CODES.includes(code) || code === FALLBACK_LANGUAGE) return { values, note: "" };

  const current = normalizeEnabled(values.enabled_languages);
  const enabled = checked
    ? normalizeEnabled([...current, code])
    : current.filter((c) => c !== code);

  if (!checked && values.default_language === code) {
    return {
      values: { ...values, enabled_languages: enabled, default_language: FALLBACK_LANGUAGE },
      note: `${languageName(code)} was the default language, so the default was reset to English.`,
    };
  }
  return { values: { ...values, enabled_languages: enabled }, note: "" };
};

export const isLanguageDirty = (values, saved) =>
  values.enable_language_support !== saved.enable_language_support ||
  values.default_language !== saved.default_language ||
  values.enabled_languages.join(",") !== saved.enabled_languages.join(",");

/** POST /config body (all three, so the toggle OFF keeps the saved list). */
export const toPayload = (values) => ({
  enable_language_support: values.enable_language_support,
  enabled_languages: normalizeEnabled(values.enabled_languages),
  default_language: values.default_language,
});
